import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, styled, Tooltip } from '@material-ui/core';
import i18next from 'i18next';

import { notUndefinedOrNull } from 'utils';
import { getCustomers } from 'services/ErplyAPI';
import { addSuccess, addWarning } from 'actions/Error';
import { setCustomer } from 'actions/customerSearch';
import { openModalPage } from 'actions/modalPage';
import { useAppDispatch } from 'reducers';
import { modalPages as mp, modalPages } from 'constants/modalPage';

import { useSseConnection } from '../connection';

import { useSuggestion } from './hooks';
import { useNotificationsCount } from 'containers/Header/index';

/**
 * Given a set of api params for the 'getCustomer' request, perform the request
 * and select the matching customer
 *
 * * If no customer is matched, shows an error
 * * If multiple customers are matched, opens ScannerMultiResults prompting the cashier to select one
 * @example
 * dispatch(selectByGetCustomer({
 *   searchNameIncrementally: 'chicory@brush.com'
 * }))
 */
function selectByGetCustomer(apiParams: object) {
  return async function action(dispatch, getState) {
    const customers = await getCustomers({
      request: 'getCustomers',
      searchFromMiddle: 0,
      ...apiParams,
      doNotCalculateRecordsTotal: 1,
    });
    if (customers.length === 1) {
      dispatch(
        addSuccess(
          i18next.t('SseApi:actions.selectCustomer.success', customers[0]),
          {
            dismissible: true,
            selfDismiss: true,
          },
        ),
      );
      dispatch(setCustomer({ data: customers[0] }));
    } else if (customers.length > 1) {
      dispatch(
        openModalPage({
          component: modalPages.ScannerMultiResults,
          isPopup: true,
          props: {
            results: {
              products: [],
              customers,
            },
          },
        }),
      );
    } else {
      dispatch(
        addWarning(i18next.t('SseApi:actions.selectCustomer.noResults'), {
          dismissible: true,
          selfDismiss: true,
        }),
      );
    }
  };
}
/** Opens the CreateCustomer form with the given customer data prefilled */
function openCreateCustomerForm(customer) {
  return openModalPage({
    component: mp.createCustomer,
    groupID: mp.createCustomer,
    props: {
      newCustomer: true,
      newCustomerData: customer,
    },
  });
}

/**
 * Component to display in the POS header.
 *
 * Manages the entire SSE behaviour
 * * Hooks up connections on the port2 side
 * * Hooks up behaviours on the port1 side
 * * Shows accept/reject buttons to the cashier when necessary
 */
export function SseHeaderComponent({render}: {render: (jsx: React.ReactNode) => React.ReactNode}) {
  const dispatch = useAppDispatch();

  // Set up connections
  useSseConnection();

  // Set up behaviours
  const { t } = useTranslation('SseApi', { keyPrefix: 'header.workflows' });
  const workflows = [
    useSuggestion('selectCustomerByEmail', ({ email }) => ({
      acceptText: t(`selectCustomerByEmail.accept`, { email }),
      rejectText: t(`selectCustomerByEmail.reject`, { email }),
      accept() {
        dispatch(
          selectByGetCustomer({
            searchNameIncrementally: email,
          }),
        );
      },
    })),
    useSuggestion('registerNewCustomer', ({ customer }) => ({
      acceptText: t(`registerNewCustomer.accept`, { customer }),
      rejectText: t(`registerNewCustomer.reject`, { customer }),
      accept() {
        dispatch(openCreateCustomerForm(customer));
      },
    })),
  ];

  // Show accept/reject buttons if necessary
  const activeSuggestion = workflows.filter(notUndefinedOrNull)[0];
  useNotificationsCount(activeSuggestion ? 1 : 0);
  if (!activeSuggestion) return null;
  return render(
    <Box display="flex" style={{gap: 8}}>
      <Tooltip title={activeSuggestion?.acceptText ?? ''}>
        <Button
          onClick={activeSuggestion.accept}
          variant="contained"
          color="secondary"
        >
          {t('accept', { keyPrefix: 'header.buttons' })}
        </Button>
      </Tooltip>
      <Tooltip title={activeSuggestion.rejectText ?? ''}>
        <Button
          onClick={activeSuggestion.reject}
          variant="outlined"
          color="secondary"
        >
          {t('reject', { keyPrefix: 'header.buttons' })}
        </Button>
      </Tooltip>
    </Box>
  );
}
